import { useEffect, useRef, type ReactNode } from 'react';
import { ICON, Icon } from './Icon.js';
import { registerSection, setSection, useSectionOpen } from './sections.js';

/**
 * A section of a side panel, which can now be put away.
 *
 * MEASURED: forty-four sections across nine panels, plus two more in `App.tsx`, all rendered into one
 * `overflow-y: auto` column that starts at 320px. `PersonPanel` alone contributes ten. Somebody who
 * opened a person to change their bucket also gets carve-out stage, stranded cost, target placement,
 * editable fields and head designation stacked underneath it, and the only way past is to scroll.
 *
 * THE MARKUP IS UNCHANGED, which is what makes forty-six call sites safe to move in one pass. The
 * root is still `<section data-testid>`, the heading is still an `<h2>`, and the classes are the ones
 * `styles.css` already carries. `.side-panel section + section` (`:449`) is the ONLY section rule in
 * the stylesheet and it is what spaces the panel — keeping `<section>` as the root element is not an
 * accident, it is the reason the spacing survives.
 *
 * `actions` IS A SEPARATE PROP, AND THAT IS THE POINT OF THE SPLIT. The headings in this product
 * already contain controls — `TransfersPanel` puts a "full width" button in one, which is why
 * `.side-panel h2 .spacer` and `.side-panel h2 button` exist. Making the whole `<h2>` the toggle
 * would swallow them: pressing "full width" would collapse the section instead of widening the
 * panel, and the failure would be silent because both are plausible outcomes of a click. So the
 * heading TEXT is the button and the actions are its siblings, exactly as they are today.
 *
 * MOUNTED ON FIRST OPEN, THEN KEPT — WHICH IS ONE DECISION, NOT TWO, and neither half works alone.
 *
 * Unmounting on every collapse would throw away somebody's unsaved work: `FieldEditor` holds a draft
 * in `useState` (`PersonPanel.tsx:731`) and `PercentBox` holds typed text (`PerimeterPanel.tsx:408`).
 * Losing a half-typed salary because you folded the section above it is the defect `live-input.tsx`
 * spends its whole header refusing.
 *
 * But rendering a section nobody has ever opened would be worse in a different way: every child
 * `data-testid` would sit in the document, so a test could assert on content no reader can see — and
 * a disclosure that had stopped working entirely would still pass every one of those assertions.
 *
 * Latching gets both. A never-opened section is genuinely absent, so a test that reaches into one
 * fails loudly and by name (`byId` lists what IS present). A section you have opened once keeps what
 * you typed in it however many times you fold it afterwards.
 *
 * THE HONEST LIMIT: this makes the panel READABLE, not faster. Once a heavy section has been opened
 * it stays mounted for the life of the panel. Anybody selling this as a performance fix is wrong.
 *
 * AND ONE COST WITH NO MITIGATION: `hidden` takes folded text out of find-in-page. In a narrow column
 * people do scan with Ctrl-F, and `hidden="until-found"` is Chromium-only and incompatible with the
 * latch above. It is a real loss and it is named here rather than discovered in a workshop.
 */
export function PanelSection({
  testId,
  title,
  actions,
  attrs,
  defaultOpen = false,
  children,
}: {
  /** Goes on the `<section>`, exactly where the hand-written copies put it. */
  testId: string;
  /** The heading's own content: the words, and any pill that is part of the label rather than a control. */
  title: ReactNode;
  /** Controls that belong in the heading. Rendered OUTSIDE the toggle — see the header. */
  actions?: ReactNode;
  /**
   * Extra attributes for the `<section>`, the way `StatTile` takes them.
   *
   * One caller needs it: the activity ticker carries `data-count`, which `App.render.test.tsx:335`
   * and the end-to-end run both read. Dropping it to fit the primitive would have been the primitive
   * deciding what a caller is allowed to say about itself.
   */
  attrs?: Record<string, string | number | undefined>;
  /**
   * What this section does before anybody has expressed a view.
   *
   * Declared here rather than in a central table because the sections are conditionally mounted and
   * the policy belongs beside the condition that mounts them. It defaults to CLOSED so a section
   * added later is quiet until somebody argues for it, which is the conservative direction for a
   * panel whose problem is that it shows too much.
   */
  defaultOpen?: boolean;
  children: ReactNode;
}): JSX.Element {
  const open = useSectionOpen(testId, defaultOpen);

  // What is on screen, so "collapse all" can mean the panel in front of you rather than every
  // section in the product. The registry counts rather than flags — see `sections.ts`.
  useEffect(() => registerSection(testId, defaultOpen), [testId, defaultOpen]);

  // Latched, not derived — see the header. Initialised from `open` so a section that starts open
  // renders on the first pass rather than one commit late.
  const everOpened = useRef(open);
  if (open) everOpened.current = true;

  const bodyId = `${testId}-body`;

  return (
    // `data-open` is what both suites read. It is on the SECTION, so a check never has to know how
    // the heading is built to ask whether the thing is folded.
    <section data-testid={testId} data-open={open ? 'yes' : 'no'} {...attrs}>
      <h2>
        <button
          type="button"
          className="sp-toggle"
          data-testid={`${testId}-toggle`}
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setSection(testId, !open)}
        >
          {/* Rotated by CSS rather than swapped for a second glyph — one meaning, one entry in `ICON`. */}
          <Icon as={ICON.disclosure} className={open ? 'sp-caret open' : 'sp-caret'} />
          {title}
        </button>
        {actions !== undefined && (
          <>
            {/* The same spacer the call sites wrote by hand, so the heading lays out identically. */}
            <span className="spacer" />
            {actions}
          </>
        )}
      </h2>
      {/*
        Always rendered, even while empty, so `aria-controls` above never dangles — a control that
        names an element the document does not have is worse than one that names nothing.
      */}
      <div className="sp-body" id={bodyId} hidden={!open}>
        {everOpened.current ? children : null}
      </div>
    </section>
  );
}
